import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { getAppSocket } from '../lib/wesSocket';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import PaginationFooter from '../components/PaginationFooter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../components/ui/breadcrumb';
import { WrenchScrewdriverIcon, PlusIcon, PencilIcon, TrashIcon, UsersIcon } from '@heroicons/react/24/outline';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';

interface Skill {
  id: number;
  value: string;
  label: string;
  isActive: boolean;
  usersCount: number;
  servicesCount: number;
}

interface UsageItem {
  id: string;
  name: string;
}

interface SkillForm {
  label: string;
}

const Skills: React.FC = () => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { register, handleSubmit, reset, formState: { errors } } = useForm<SkillForm>();
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageSkill, setUsageSkill] = useState<Skill | null>(null);
  const [usageUsers, setUsageUsers] = useState<UsageItem[]>([]);
  const [usageServices, setUsageServices] = useState<UsageItem[]>([]);

  const fetchSkills = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get('/api/skills');
      setSkills(response.data.skills || []);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Error al cargar las skills');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();

    const handleKeyPress = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'n' || e.key === 'N') {
        if (!showForm) {
          setSelectedSkill(null);
          reset({ label: '' });
          setShowForm(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showForm, reset]);

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !path.startsWith('/api/skills')) return;

      if (showForm) {
        pendingRefreshRef.current = true;
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchSkills({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [showForm]);

  useEffect(() => {
    if (showForm) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    fetchSkills({ silent: true });
  }, [showForm]);

  const onSubmit = async (data: SkillForm) => {
    try {
      const payload = { label: data.label.trim() };
      if (selectedSkill) {
        await axios.put(`/api/skills/${selectedSkill.id}`, payload);
        toast.success('Skill actualizada');
      } else {
        await axios.post('/api/skills', payload);
        toast.success('Skill creada');
      }
      reset({ label: '' });
      setSelectedSkill(null);
      setShowForm(false);
      fetchSkills();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar la skill');
    }
  };

  const handleEdit = (skill: Skill) => {
    setSelectedSkill(skill);
    setShowForm(true);
    reset({ label: skill.label });
  };

  const handleDelete = async (id: number) => {
    if (!await confirmDialog('Eliminar esta skill? Dejara de aparecer al asignar skills a usuarios y productos. Las asignaciones existentes no se modifican.')) return;
    try {
      await axios.delete(`/api/skills/${id}`);
      toast.success('Skill eliminada');
      fetchSkills();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar la skill');
    }
  };

  const handleCancel = () => {
    reset({ label: '' });
    setSelectedSkill(null);
    setShowForm(false);
  };

  const toggleActive = async (skill: Skill) => {
    setTogglingId(skill.id);
    // Optimista
    setSkills((prev) => prev.map((s) => s.id === skill.id ? { ...s, isActive: !s.isActive } : s));
    try {
      await axios.put(`/api/skills/${skill.id}`, { isActive: !skill.isActive });
    } catch (error: any) {
      // Revertir
      setSkills((prev) => prev.map((s) => s.id === skill.id ? { ...s, isActive: skill.isActive } : s));
      toast.error(error.response?.data?.error || 'No se pudo cambiar el estado');
    } finally {
      setTogglingId(null);
    }
  };

  const openUsage = async (skill: Skill) => {
    setUsageSkill(skill);
    setUsageUsers([]);
    setUsageServices([]);
    setUsageOpen(true);
    setUsageLoading(true);
    try {
      const res = await axios.get(`/api/skills/${skill.id}/usage`);
      setUsageUsers(res.data?.users || []);
      setUsageServices(res.data?.services || []);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo cargar el detalle');
    } finally {
      setUsageLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button onClick={() => navigate('/settings')} className="text-left">
                Configuración
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Skills</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Skills</h1>
          <p className="text-sm text-muted-foreground">Especialidades que se asignan a usuarios y se requieren en productos/servicios.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/settings')} size="sm">
            Volver
          </Button>
          <Button
            onClick={() => { setSelectedSkill(null); reset({ label: '' }); setShowForm(true); }}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            size="sm"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nueva skill
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </Button>
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedSkill ? 'Editar skill' : 'Nueva skill'}</DialogTitle>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Nombre de la skill *</Label>
              <Input
                {...register('label', { required: 'Nombre requerido' })}
                placeholder="ej: Mecanico, Gomeria, Pintura..."
                autoFocus
              />
              {errors.label && <p className="text-xs text-destructive">{errors.label.message}</p>}
              {selectedSkill && (
                <p className="text-xs text-muted-foreground">Identificador interno: <span className="font-mono">{selectedSkill.value}</span> (no se modifica)</p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={handleCancel}>Cancelar</Button>
              <Button type="submit" className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-gray-100">
                {selectedSkill ? 'Guardar cambios' : 'Crear skill'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Uso de "{usageSkill?.label}"</DialogTitle>
          </DialogHeader>
          {usageLoading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Usuarios ({usageUsers.length})</h4>
                {usageUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ningun usuario tiene esta skill.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {usageUsers.map((u) => (
                      <Badge key={u.id} variant="secondary">{u.name}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Servicios ({usageServices.length})</h4>
                {usageServices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ningun servicio requiere esta skill.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {usageServices.map((s) => (
                      <Badge key={s.id} variant="secondary">{s.name}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setUsageOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : skills.length === 0 ? (
            <div className="text-center py-12">
              <WrenchScrewdriverIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay skills creadas</p>
            </div>
          ) : (
            <div className="hidden md:block relative w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Identificador</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Usos</TableHead>
                    <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skills
                    .slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize)
                    .map((skill) => (
                    <TableRow key={skill.id} className="border-b hover:bg-muted/50">
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="font-medium">{skill.label}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <Badge variant="secondary" className="font-mono">{skill.value}</Badge>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={skill.isActive}
                            disabled={togglingId === skill.id}
                            onCheckedChange={() => toggleActive(skill)}
                          />
                          <span className="text-sm text-muted-foreground">{skill.isActive ? 'Activa' : 'Inactiva'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <Tip label="Ver a quien se asigno">
                        <button
                          type="button"
                          onClick={() => openUsage(skill)}
                          className="text-sm underline-offset-2 hover:underline"
                        >
                          {skill.usersCount} usuario{skill.usersCount === 1 ? '' : 's'} · {skill.servicesCount} servicio{skill.servicesCount === 1 ? '' : 's'}
                        </button>
                        </Tip>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                        <div className="flex items-center justify-end">
                          <ButtonGroup>
                            <Tip label="Ver detalle de uso">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openUsage(skill)}
                              className="size-8"
                            >
                              <UsersIcon className="h-4 w-4" />
                            </Button>
                            </Tip>
                            <Tip label="Editar skill">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleEdit(skill)}
                              className="size-8"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            </Tip>
                            <Tip label="Eliminar skill">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDelete(skill.id)}
                              className="size-8"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                            </Tip>
                          </ButtonGroup>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaginationFooter
        currentPage={currentPage}
        totalPages={Math.max(1, Math.ceil(skills.length / pageSize))}
        totalResults={skills.length}
        startResult={(currentPage - 1) * pageSize + 1}
        onFirst={() => setCurrentPage(1)}
        onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
        onNext={() => setCurrentPage((p) => Math.min(Math.max(1, Math.ceil(skills.length / pageSize)), p + 1))}
        onLast={() => setCurrentPage(Math.max(1, Math.ceil(skills.length / pageSize)))}
      />
    </div>
  );
};

export default Skills;
